import type { PlaylistEntry } from './PlaylistController'
import { audiobookAsin } from './audiobookAsin'
import type { Maybe } from '@amazon/vinyl-util'

/**
 * Entry-kind classification for the playback layer and the transport control row.
 *
 * A video podcast is BOTH a `PodcastEpisode` and `video === true`, so the two
 * predicates deliberately overlap only on the podcast side: a video podcast is
 * a podcast (gets the 15/30 jump pair + playback-rate), NOT a VOD video (does
 * not also get the VOD 10/10 jump pair). VOD-only video is the non-podcast
 * `video === true` case. Keeping this here keeps Transport and FullPlayer from
 * drifting on the gate.
 */
export function isPodcastEntry(entry: Maybe<PlaylistEntry>): boolean {
    return entry?.entity.__typename === 'PodcastEpisode'
}

/** A free-tier queue ad. */
export function isAdvertisementEntry(entry: Maybe<PlaylistEntry>): boolean {
    return entry?.entity.__typename === 'Advertisement'
}

export function isVodVideoEntry(entry: Maybe<PlaylistEntry>): boolean {
    return entry?.video === true && !isPodcastEntry(entry)
}

/**
 * A standalone ASIN music track — the only audio kind the streaming-quality
 * gear applies to. Podcasts, audiobooks, and videos own their own settings; a
 * music-video Track carries `mediaType === 'VIDEO'`, so it routes to the video
 * gear rather than this one.
 */
export function isAsinTrackEntry(entry: Maybe<PlaylistEntry>): boolean {
    const entity = entry?.entity
    return entity?.__typename === 'Track' && entity.mediaType !== 'VIDEO'
}

/** A catalog track, video or not — the only entry kind the gateway holds lyrics for. */
export function isTrackEntry(entry: Maybe<PlaylistEntry>): boolean {
    return entry?.entity.__typename === 'Track'
}

/**
 * An audiobook entry — the transport steps chapters (sections) through the
 * player's section-nav surface for these instead of the single-entry feed
 * (GH parity). Detected by a resolvable audiobook asin, the same signal the
 * Audible engine activates on.
 */
export function isAudiobookEntry(entry: Maybe<PlaylistEntry>): boolean {
    return entry != null && audiobookAsin(entry) !== null
}
